import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Save,
  CheckCircle,
  AlertCircle,
  Users,
  Award,
  Loader2,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

type Avis = 'tres_favorable' | 'favorable' | 'reserve' | 'defavorable';

const AVIS_OPTIONS: { value: Avis; label: string; color: string }[] = [
  { value: 'tres_favorable', label: 'Très favorable', color: 'text-success-700 bg-success-50' },
  { value: 'favorable', label: 'Favorable', color: 'text-primary-700 bg-primary-50' },
  { value: 'reserve', label: 'Réservé', color: 'text-amber-700 bg-amber-50' },
  { value: 'defavorable', label: 'Défavorable', color: 'text-error-700 bg-error-50' },
];

type StudentRow = {
  id: string;
  first_name: string;
  last_name: string;
  session_examen: number | null;
  numero_candidat: string | null;
  class_name: string | null;
};

type ConseilEntry = {
  avis: Avis | null;
  observations: string;
  pix_certifie: boolean;
  cc1_note: string; // kept as string for input
  cc2_note: string;
  existing_id?: string;
  dirty: boolean;
  saving: boolean;
  saved_at?: Date;
  error?: string | null;
};

const emptyEntry = (): ConseilEntry => ({
  avis: null,
  observations: '',
  pix_certifie: false,
  cc1_note: '',
  cc2_note: '',
  dirty: false,
  saving: false,
});

const AdminConseilClasse = () => {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useAuth();
  const [className, setClassName] = useState<string>('');
  const [sessionYear, setSessionYear] = useState<number>(new Date().getFullYear());
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [entries, setEntries] = useState<Record<string, ConseilEntry>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    if (!classId) return;
    void load();
  }, [classId, sessionYear]);

  const load = async () => {
    try {
      setIsLoading(true);
      setGlobalError(null);

      // Classe
      const { data: cls, error: clsErr } = await supabase
        .from('classes')
        .select('id, name, class_number, period')
        .eq('id', classId)
        .maybeSingle();
      if (clsErr) throw clsErr;
      if (cls) {
        setClassName(`${cls.name} ${cls.class_number} (${cls.period})`);
      }

      // Étudiants de la classe
      const { data: studentsData, error: studErr } = await supabase
        .from('students')
        .select('id, first_name, last_name, session_examen, numero_candidat, class:classes(name)')
        .eq('class_id', classId)
        .order('last_name');
      if (studErr) throw studErr;

      const rows: StudentRow[] = (studentsData ?? []).map((s: any) => ({
        id: s.id,
        first_name: s.first_name,
        last_name: s.last_name,
        session_examen: s.session_examen,
        numero_candidat: s.numero_candidat,
        class_name: s.class?.name ?? null,
      }));
      setStudents(rows);

      if (rows.length > 0) {
        const mostCommonSession = rows.reduce(
          (acc, r) => (r.session_examen ? acc + r.session_examen : acc),
          0
        );
        const nonNull = rows.filter((r) => r.session_examen).length;
        if (nonNull > 0) {
          const avgSession = Math.round(mostCommonSession / nonNull);
          if (avgSession !== sessionYear) setSessionYear(avgSession);
        }
      }

      // Données existantes
      if (rows.length > 0) {
        const ids = rows.map((r) => r.id);
        const { data: existing, error: cErr } = await supabase
          .from('livret_conseil_classe')
          .select('*')
          .in('student_id', ids)
          .eq('session_year', sessionYear);
        if (cErr) throw cErr;

        const map: Record<string, ConseilEntry> = {};
        for (const r of rows) {
          const found = (existing ?? []).find((e) => e.student_id === r.id);
          if (found) {
            map[r.id] = {
              avis: found.avis as Avis | null,
              observations: found.observations ?? '',
              pix_certifie: !!found.pix_certifie,
              cc1_note: found.cc1_note != null ? String(found.cc1_note) : '',
              cc2_note: found.cc2_note != null ? String(found.cc2_note) : '',
              existing_id: found.id,
              dirty: false,
              saving: false,
            };
          } else {
            map[r.id] = emptyEntry();
          }
        }
        setEntries(map);
      } else {
        setEntries({});
      }
    } catch (err) {
      console.error('Error loading conseil:', err);
      setGlobalError("Erreur lors du chargement du conseil de classe.");
    } finally {
      setIsLoading(false);
    }
  };

  const updateEntry = (studentId: string, patch: Partial<ConseilEntry>) => {
    setEntries((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], ...patch, dirty: true, error: null },
    }));
  };

  const validateNote = (value: string): string | null => {
    if (!value.trim()) return null;
    const n = Number(value.replace(',', '.'));
    if (Number.isNaN(n)) return 'Note invalide';
    if (n < 0 || n > 20) return 'Note entre 0 et 20';
    return null;
  };

  const saveOne = async (studentId: string): Promise<boolean> => {
    const entry = entries[studentId];
    if (!entry) return false;

    // Validation
    const err1 = validateNote(entry.cc1_note);
    const err2 = validateNote(entry.cc2_note);
    if (err1 || err2) {
      setEntries((p) => ({
        ...p,
        [studentId]: { ...p[studentId], error: err1 ?? err2 },
      }));
      return false;
    }

    setEntries((p) => ({ ...p, [studentId]: { ...p[studentId], saving: true, error: null } }));

    try {
      const payload = {
        student_id: studentId,
        session_year: sessionYear,
        avis: entry.avis,
        observations: entry.observations.trim() || null,
        pix_certifie: entry.pix_certifie,
        cc1_note: entry.cc1_note.trim() ? Number(entry.cc1_note.replace(',', '.')) : null,
        cc2_note: entry.cc2_note.trim() ? Number(entry.cc2_note.replace(',', '.')) : null,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      let upsertErr;
      if (entry.existing_id) {
        const { error } = await supabase
          .from('livret_conseil_classe')
          .update(payload)
          .eq('id', entry.existing_id);
        upsertErr = error;
      } else {
        const { data, error } = await supabase
          .from('livret_conseil_classe')
          .insert(payload)
          .select('id')
          .single();
        upsertErr = error;
        if (data?.id) {
          setEntries((p) => ({
            ...p,
            [studentId]: { ...p[studentId], existing_id: data.id },
          }));
        }
      }
      if (upsertErr) throw upsertErr;

      setEntries((p) => ({
        ...p,
        [studentId]: {
          ...p[studentId],
          saving: false,
          dirty: false,
          saved_at: new Date(),
          error: null,
        },
      }));
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erreur inconnue';
      setEntries((p) => ({
        ...p,
        [studentId]: { ...p[studentId], saving: false, error: msg },
      }));
      return false;
    }
  };

  const saveAllDirty = async () => {
    setSavingAll(true);
    const dirtyIds = Object.entries(entries)
      .filter(([_, v]) => v.dirty)
      .map(([id]) => id);
    for (const id of dirtyIds) {
      await saveOne(id);
    }
    setSavingAll(false);
  };

  const filledCount = Object.values(entries).filter((e) => e.avis !== null).length;
  const dirtyCount = Object.values(entries).filter((e) => e.dirty).length;

  return (
    <div className="animate-fade-in">
      <header className="mb-8">
        <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
          <ChevronLeft className="h-4 w-4 mr-1" />
          <span>Retour au tableau de bord</span>
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <Award className="h-7 w-7 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">Conseil de classe</h1>
        </div>
        <p className="text-gray-600">
          {className && <span className="font-medium">{className} — </span>}
          Saisie des avis, observations et notes de contrôle continu pour le livret scolaire.
        </p>
      </header>

      {globalError && (
        <div className="bg-error-50 border border-error-200 text-error-700 p-4 rounded-md mb-6 flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          {globalError}
        </div>
      )}

      <div className="mb-6 bg-white rounded-lg shadow p-4 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <label htmlFor="session-year" className="text-xs text-gray-500 block mb-1">
              Session d'examen
            </label>
            <select
              id="session-year"
              value={sessionYear}
              onChange={(e) => setSessionYear(Number(e.target.value))}
              className="input-field py-1.5"
            >
              {[sessionYear - 2, sessionYear - 1, sessionYear, sessionYear + 1].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-600">
            <Users className="inline h-4 w-4 mr-1" />
            {students.length} étudiant{students.length > 1 ? 's' : ''} —{' '}
            <span className="font-medium">{filledCount}</span> avis renseigné
            {filledCount > 1 ? 's' : ''}
          </div>
        </div>
        <button
          onClick={saveAllDirty}
          disabled={savingAll || dirtyCount === 0}
          className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {savingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer tout ({dirtyCount})
        </button>
      </div>

      {isLoading ? (
        <div className="p-12 text-center text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-600" />
        </div>
      ) : students.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Aucun étudiant dans cette classe.
        </div>
      ) : (
        <div className="space-y-4">
          {students.map((s) => {
            const e = entries[s.id] ?? emptyEntry();
            const displayName = `${s.last_name.toUpperCase()} ${s.first_name}`;
            return (
              <div
                key={s.id}
                className={`bg-white rounded-lg shadow border-l-4 ${
                  e.avis === 'tres_favorable'
                    ? 'border-success-500'
                    : e.avis === 'favorable'
                    ? 'border-primary-500'
                    : e.avis === 'reserve'
                    ? 'border-amber-500'
                    : e.avis === 'defavorable'
                    ? 'border-error-500'
                    : 'border-gray-200'
                } p-5`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">{displayName}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {s.numero_candidat ? `N° candidat ${s.numero_candidat}` : 'N° candidat non attribué'}
                      {' · '}
                      Session {s.session_examen ?? '—'}
                    </p>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    {e.saved_at && !e.dirty && (
                      <span className="text-xs text-success-600 inline-flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        enregistré
                      </span>
                    )}
                    {e.dirty && (
                      <span className="text-xs text-amber-600">non enregistré</span>
                    )}
                    <button
                      onClick={() => saveOne(s.id)}
                      disabled={!e.dirty || e.saving}
                      className="text-xs bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                    >
                      {e.saving ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                      Enregistrer
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Avis du conseil
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {AVIS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() =>
                            updateEntry(s.id, {
                              avis: e.avis === opt.value ? null : opt.value,
                            })
                          }
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                            e.avis === opt.value
                              ? `${opt.color} border-transparent ring-2 ring-offset-1 ring-current`
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Note CC1 /20
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      max="20"
                      value={e.cc1_note}
                      onChange={(ev) => updateEntry(s.id, { cc1_note: ev.target.value })}
                      placeholder="—"
                      className="input-field py-1.5 w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Note CC2 /20
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      max="20"
                      value={e.cc2_note}
                      onChange={(ev) => updateEntry(s.id, { cc2_note: ev.target.value })}
                      placeholder="—"
                      className="input-field py-1.5 w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Observations
                    </label>
                    <textarea
                      value={e.observations}
                      onChange={(ev) => updateEntry(s.id, { observations: ev.target.value })}
                      rows={2}
                      placeholder="Remarques, points forts/axes de travail..."
                      className="input-field w-full"
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-400 mt-0.5">
                      {e.observations.length}/500 caractères
                    </p>
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={e.pix_certifie}
                        onChange={(ev) => updateEntry(s.id, { pix_certifie: ev.target.checked })}
                        className="h-4 w-4 rounded"
                      />
                      <span>Certification PIX obtenue</span>
                    </label>
                  </div>
                </div>

                {e.error && (
                  <div className="mt-3 text-xs text-error-700 bg-error-50 rounded p-2 flex items-start">
                    <AlertCircle className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                    {e.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminConseilClasse;
